// import/no-extraneous-dependencies
/* eslint-disable camelcase */
/* eslint jsx-a11y/label-has-associated-control: ["error", { assert: "either", depth: 25 }] */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import Modal from './Modal';
import EventButton from './Buttons/EventButton';

function IdentificationsTable({ title, content: ContentComponent, srm }) {
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);

  // Handle the actual data structure from the API
  const actions = useMemo(() => srm?.actions_taken || [], [srm?.actions_taken]);
  const causes = useMemo(() => srm?.causes || [], [srm?.causes]);
  const systemStates = useMemo(() => srm?.system_states || [], [srm?.system_states]);

  // Check for alternative data sources
  const safetyRecommendations = useMemo(() => srm?.safety_recommendation_status
  || [], [srm?.safety_recommendation_status]);

  const [content, setContent] = useState([]);

  // Helper function to get priority class
  const getPriorityClass = (priority) => {
    if (priority === 0) return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    if (priority === 1) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
  };

  // Helper function to get column header
  const getColumnHeader = () => {
    if (title === 'Causes') return 'Sub Cause Name';
    if (title === 'Preliminary actions' && safetyRecommendations.length > 0) return 'Assigned To';
    return 'By';
  };

  const handleEditClick = (item) => {
    setEditItem(item);
    setShowModal(true);
  };
  const handleAddClick = () => {
    setEditItem(null);
    setShowModal(true);
  };
  useEffect(() => {
    let tableContent;
    if (title === 'Preliminary actions') {
      tableContent = actions.map((action) => (
        <tr key={action.id}>
          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
            {action.description}
          </td>
          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
            {action.actionReportedBy}
          </td>
          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
            <button
              type="button"
              id={action.id}
              onClick={() => handleEditClick(action)}
              className="flex w-full items-center py-2 px-4 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white text-gray-700 dark:text-gray-200"
            >
              <svg
                aria-hidden="true"
                className="mr-1 -ml-1 w-5 h-5"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                <path
                  fillRule="evenodd"
                  d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
                  clipRule="evenodd"
                />
              </svg>
              Edit
            </button>
          </td>
          <td className="px-4 py-3 flex items-center justify-end" />
        </tr>
      ));
    } else if (title === 'Causes') {
      tableContent = causes.map((cause) => (
        <tr key={cause.id}>
          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
            {cause.main_cause_name}
          </td>
          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
            {cause.sub_cause_name}
          </td>
          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
            <button
              type="button"
              id={cause.id}
              onClick={() => handleEditClick(cause)}
              className="flex w-full items-center py-2 px-4 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white text-gray-700 dark:text-gray-200"
            >
              <svg
                aria-hidden="true"
                className="mr-1 -ml-1 w-5 h-5"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                <path
                  fillRule="evenodd"
                  d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
                  clipRule="evenodd"
                />
              </svg>
              Edit
            </button>
          </td>
          <td className="px-4 py-3 flex items-center justify-end" />
        </tr>
      ));
    } else if (title === 'System states') {
      tableContent = systemStates.map((systemState) => (
        <tr key={systemState.id}>
          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
            {systemState.details}
          </td>
          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
            {systemState.reportedBy}
          </td>
          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
            <button
              type="button"
              id={systemState.id}
              onClick={() => handleEditClick(systemState)}
              className="flex w-full items-center py-2 px-4 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white text-gray-700 dark:text-gray-200"
            >
              <svg
                aria-hidden="true"
                className="mr-1 -ml-1 w-5 h-5"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                <path
                  fillRule="evenodd"
                  d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
                  clipRule="evenodd"
                />
              </svg>
              Edit
            </button>
          </td>
          <td className="px-4 py-3 flex items-center justify-end" />
        </tr>
      ));
    }

    // If we have safety recommendations but no traditional SRM data, show them
    if (title === 'Preliminary actions' && actions.length === 0 && safetyRecommendations.length > 0) {
      tableContent = safetyRecommendations.map((rec, index) => {
        // Clean HTML content and extract text
        const cleanDetails = rec.details ? rec.details.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : '';
        const truncatedDetails = cleanDetails.length > 100 ? `${cleanDetails.substring(0, 100)}...` : cleanDetails;

        return (
          <tr key={`safety-rec-${rec.id || index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
            <td className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100">
              <div className="max-w-md">
                <p className="font-medium text-gray-900 dark:text-white mb-1">
                  Safety Recommendation
                  {' '}
                  {index + 1}
                </p>
                <p className="text-gray-600 dark:text-gray-300 text-xs leading-relaxed">
                  {truncatedDetails}
                </p>
                {rec.typeOfControl && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 mt-2">
                    {rec.typeOfControl}
                  </span>
                )}
              </div>
            </td>
            <td className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100">
              <div className="flex flex-col">
                <span className="font-medium">{rec.actionBy || 'Not assigned'}</span>
                {rec.deadline && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Due:
                    {' '}
                    {new Date(rec.deadline).toLocaleDateString()}
                  </span>
                )}
              </div>
            </td>
            <td className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100">
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityClass(rec.priority)}`}>
                  Priority
                  {' '}
                  {rec.priority}
                </span>
              </div>
            </td>
            <td className="px-4 py-4 text-right text-sm font-medium">
              <button
                type="button"
                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
              >
                View Details
              </button>
            </td>
          </tr>
        );
      });
    }

    setContent(tableContent);
  }, [title, actions, causes, systemStates, safetyRecommendations]);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-500">
            {title}
          </h1>
        </div>
        <div className="mt-4 pt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <EventButton onSubmit={handleAddClick} linktext={`Add ${title}`} />
          {showModal && (
            <Modal
              editItem={editItem || ''}
              title={title}
              content={ContentComponent}
              srm={srm}
              onClose={() => setShowModal(false)}
            />
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        {content.length !== 0 && (
          <table className="w-full my-2 text-sm text-left text-gray-400 dark:text-gray-400">
            <thead className="text-xs text-gray-300 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="px-4 py-3">
                  {title === 'Preliminary actions' && safetyRecommendations.length > 0 ? 'Safety Recommendation' : 'Description'}
                </th>
                <th scope="col" className="px-4 py-3">
                  {getColumnHeader()}
                </th>
                <th scope="col" className="px-4 py-3">
                  {title === 'Preliminary actions' && safetyRecommendations.length > 0 ? 'Priority' : 'Actions'}
                </th>
                <th scope="col" className="px-4 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {content}
            </tbody>
          </table>
        )}
        {content.length === 0 && (
          <p className="text-sm text-gray-500">
            No
            {title.toLowerCase()}
            {' '}
            available.
          </p>
        )}
      </div>
    </div>
  );
}

IdentificationsTable.propTypes = {
  title: PropTypes.string.isRequired,
  content: PropTypes.elementType.isRequired,
  // srm: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  srm: PropTypes.object.isRequired,
};

// IdentificationsTable.defaultProps = {
//   srm: null,
// };

export default IdentificationsTable;
export const handleSave = async (endpoint, formData, method = 'POST') => {
  try {
    // Get token from localStorage
    const token = localStorage.getItem('ans-sms');
    const authToken = token ? JSON.parse(token).token : null;

    const headers = {
      'Content-Type': 'application/json',
      ...(authToken && { Authorization: `Bearer ${authToken}` }),
    };

    if (method === 'POST') {
      await axios.post(`http://localhost:8080/${endpoint}`, formData, { headers });
      window.location.reload(false);
    } else if (method === 'PATCH') {
      await axios.patch(`http://localhost:8080/${endpoint}`, formData, { headers });
      window.location.reload(false);
      window.scrollTo(0, window.location);
    }
  } catch (error) {
    // Error handling for API requests
  }
};
